import { useNavigate, useLocation } from "react-router-dom";
import { Play, X } from "lucide-react";
import { playerStore, useMiniPlayer } from "../lib/playerStore.js";

export function FloatingMiniPlayer(): JSX.Element | null {
  const nav = useNavigate();
  const location = useLocation();
  const state = useMiniPlayer();

  if (!state) return null;

  // Hide when on the exact watch page for this item
  if (location.pathname === `/watch/${encodeURIComponent(state.itemId)}`) return null;

  const progress = state.duration > 0 ? Math.min(100, (state.position / state.duration) * 100) : 0;

  return (
    <div className="fixed bottom-4 right-4 z-50 w-64 rounded-xl overflow-hidden shadow-2xl border border-hairline bg-lacquer">
      <div className="relative aspect-video bg-ink">
        {state.thumbnail && (
          <img
            src={state.thumbnail}
            alt=""
            className="w-full h-full object-cover opacity-60"
            referrerPolicy="no-referrer"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-lacquer/70 to-transparent" />

        {/* Progress bar */}
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/20">
          <div className="h-full bg-vermilion transition-all" style={{ width: `${progress}%` }} />
        </div>

        {/* Resume button */}
        <button
          onClick={() => nav(`/watch/${encodeURIComponent(state.itemId)}?autoplay=1`)}
          className="absolute inset-0 flex items-center justify-center group"
          aria-label="Resume playback"
        >
          <div className="w-11 h-11 rounded-full bg-black/50 flex items-center justify-center group-hover:bg-vermilion/80 transition-colors">
            <Play size={20} className="text-ivory ml-0.5" />
          </div>
        </button>

        {/* Dismiss */}
        <button
          onClick={() => playerStore.set(null)}
          className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80 transition-colors"
          aria-label="Dismiss"
        >
          <X size={12} className="text-ivory" />
        </button>
      </div>

      <div className="px-3 py-2">
        <div className="text-ivory text-xs font-display truncate leading-snug">{state.title}</div>
        <div className="text-parchment/50 text-[10px] mt-0.5 font-mono">{fmtTime(state.position)} · paused</div>
      </div>
    </div>
  );
}

function fmtTime(s: number): string {
  if (s < 3600) return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}:${String(m).padStart(2, "0")}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
}
